import React, { useEffect, useState } from 'react';
import cn from "classnames";
import { partial, times } from "lodash";
import makeStyles from "@mui/styles/makeStyles";
import { Grid, Typography } from '@mui/material';

import ChipList from '@dyl/components/ChipList';
import RubricBlock from '@dyl/blocks/RubricBlock';
import SimpleTaskField from '@dyl/blocks/SimpleTaskField';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import SectionDividers from "@dyl/components/SectionDividers";
import useLocalTaskError from '@dyl/pages/Tasks/edit/useLocalTaskError';
import DragCanvas, { DragCanvasBubble } from "@dyl/components/DragCanvas";
import { useImage } from "@dyl/systems/ImagesManager";
import TaskRadioImagesBlock from "@dyl/blocks/TaskRadioImagesBlock";


const CHIPLIST_CHARACTER_LIMIT = 750;
const COORDS = [
    { x: 570, y: 255 },
    { x: 1650, y: 255 },
    { x: 570, y: 685 },
    { x: 1650, y: 695 },
];

const useStyles = makeStyles(theme => ({
    root: {
        maxWidth: theme.breakpoints.values.md,
    },

    itemHeader:  {
        marginBottom: '16px',
    },
    partHeader: {
        marginBottom: '16px',
        textAlign: 'center'
    },
    groupHeader: {
        marginBottom: '16px',
    },
    groupGrid: {
        marginBottom: '32px'
    },
    newHeader: {
        marginTop: '16px',
        marginBottom: '16px',
        color: '#757575',

    }
}));

export default function TaskItems({
    className: propsClass,
    ...other
}) {
    const classes = useStyles();
    const [items] = useTaskField('items');

    const phaseBreakPoints = items.map((a, i) => items[i - 1]?.phase !== a.phase);
    const groupBreakPoints = items
        .filter(x => x.phase === 'PHASE_1')
        .map((a, i) => items[i - 1]?.group !== a.group);

    return (
        <div className={cn(classes.root, propsClass)} {...other}>
            <SectionDividers>
                {times(items.length, i =>
                    <Grid key={i}>
                        {!!phaseBreakPoints[i] &&
                            <Typography variant='h4' className={classes.partHeader}>
                                {`Part ${phaseBreakPoints.slice(0, i + 1).reduce((acc, item) => acc += item ? 1 : 0, 0)}`}
                            </Typography>}
                        {!!groupBreakPoints[i] && <Group index={i} />}
                        {items[i].phase === 'PHASE_1' ? <ItemPhase1 index={i} /> : <ItemPhase2 index={i}/> }
                    </Grid>
                )}
            </SectionDividers>
        </div>
    );
}



function Group({ index }) {
    const classes = useStyles();
    const useField = partial(useTaskField, 'items', index);
    const [group] = useField('group');

    const [backgroundImageId] = useTaskField('background_image');
    const { src: backgroundImageSrc } = useImage(backgroundImageId);

    const [narrative] = useField('stimulus_text');
    const [bubble1] = useField('speech_bubble_1');
    const [bubble2] = useField('speech_bubble_2');

    const [coords1, setCoords1] = useField('speech_bubble_1_coordinates');
    const [coords2, setCoords2] = useField('speech_bubble_2_coordinates');

    const coords = COORDS[group - 1];
    const targetDimensions = { width: 1024, height: 400 };
    const left = targetDimensions.width / 2 - coords.x;
    const top = targetDimensions.height / 2 - coords.y;

    return (<>
        <Typography variant='h5' className={classes.groupHeader}>
            {`Group ${group}`}
        </Typography>

        <Grid container spacing={2} className={classes.groupGrid}>
            <Grid item xs={12}>
                <SimpleTaskField
                    path={['items', index, 'stimulus_text']}
                    label='Narrative'
                />
            </Grid>

            <Grid item xs={12} sm={6}>
                <SimpleTaskField
                    path={['items', index, 'speech_bubble_1']}
                    label='Speech Bubble 1'
                    required
                />
            </Grid>

            <Grid item xs={12} sm={6}>
                <SimpleTaskField
                    path={['items', index, 'speech_bubble_2']}
                    label='Speech Bubble 2'
                    required
                />
            </Grid>

            <Grid>
                <DragCanvas initCoords={{ top, left }} targetDimensions={targetDimensions} backgroundSrc={backgroundImageSrc}>
                    {narrative && <DragCanvasBubble coordinates={{ top: 0, left: 0 }} text={narrative} style={{ width: '290px' }} disabled/>}
                    {bubble1 && <DragCanvasBubble coordinates={coords1} onCoordinatesChange={setCoords1} text={bubble1}/>}
                    {bubble2 && <DragCanvasBubble coordinates={coords2} onCoordinatesChange={setCoords2} text={bubble2}/>}
                </DragCanvas>
            </Grid>
        </Grid>
    </>);
}

function ItemPhase1({ index }) {
    const classes = useStyles();
    const [answers, setAnswers, answersError] = useTaskField('items', index, 'answers', 0, 'key');

    const [q1] = useTaskField('items', index, 'question_1');
    const [shouldShowQ1Error, setShowQ1Error] = useState(false);
    const q1Error = (
        (!q1 && 'Required.')
        || ''
    );

    useLocalTaskError(q1Error, () => setShowQ1Error(true));
    useEffect(() => {
        if (!q1Error) {
            setShowQ1Error(false);
        }
    }, [q1Error]);

    return (
        <>
            <Typography variant='h6' className={classes.itemHeader}>
                {index === 0 ? 'Example' : `Item ${index}`}
            </Typography>

            {index === 0 && <RubricBlock />}

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <SimpleTaskField
                        path={['items', index, 'question_1']}
                        label='Question (and prompt) (left)'
                        helperText={(shouldShowQ1Error && q1Error) || 'Question text that appears before the answer box.'}
                        error={shouldShowQ1Error && !!q1Error}
                        required
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <SimpleTaskField
                        path={['items', index, 'question_2']}
                        label='Prompt (right)'
                        helperText='Question text that appears after the answer box.'
                    />
                </Grid>
                <Grid item xs={12}>
                    <ChipList
                        value={answers}
                        onChange={setAnswers}
                        label='Correct Answers'
                        error={!!answersError}
                        helperText={
                            answersError
                            || `Answers that will be accepted as correct${index === 0 ? ' (max 1 for example)' : ''}.`}
                        required
                        max={index === 0 ? 1 : undefined}
                        maxLen={CHIPLIST_CHARACTER_LIMIT}
                        isComicStoryA1 = {true}
                    />
                </Grid>
            </Grid>
        </>
    );
}

function ItemPhase2({ index }) {
    const classes = useStyles();
    const CHOICE_KEYWORDS_LIMIT = 750;
    return (
        <>
            <Typography variant='h6' className={classes.itemHeader}>
                {index === 0 ? 'Example' : `Item ${index}`}
            </Typography>

            {index === 7 && <RubricBlock index={index}/>}

            <TaskRadioImagesBlock path={['items', index, 'answers']} hasCheckbox={false}/>
            <Typography variant='h5' className={classes.newHeader}>
                {`Keywords`}
            </Typography>
            <SimpleTaskField
                required
                label='Choice A keyword(s)'
                path={['items', index, 'answers', 0, 'key']}
                helperText='Please type the keywords, delimited by commas.'
                limit = { CHOICE_KEYWORDS_LIMIT }
            />
            <SimpleTaskField
                required
                label='Choice B keyword(s)'
                path={['items', index, 'answers', 1, 'key']}
                helperText='Please type the keywords, delimited by commas.'
                limit = { CHOICE_KEYWORDS_LIMIT }
            />
            <SimpleTaskField
                required
                label='Choice C keyword(s)'
                path={['items', index, 'answers', 2, 'key']}
                helperText='Please type the keywords, delimited by commas.'
                limit = { CHOICE_KEYWORDS_LIMIT }
            />

            <Typography variant='h5' className={classes.newHeader}>
                {`Training Sentences`}
            </Typography>

            <Grid item xs={12}>
                <SimpleTaskField
                    required
                    label='Choice A Training Sentences'
                    path={['items', index, 'answers', 0, 'training_sentences']}
                    helperText='Please type at least 5 training sentences delimited by commas.'
                    multiline
                    rows={3}
                />
            </Grid>
            <Grid item xs={12}>
                <SimpleTaskField
                    required
                    label='Choice B Training Sentences'
                    path={['items', index, 'answers', 1, 'training_sentences']}
                    helperText='Please type at least 5 training sentences delimited by commas.'
                    multiline
                    rows={3}
                />
            </Grid>
            <Grid item xs={12}>
                <SimpleTaskField
                    required
                    label='Choice C Training Sentences'
                    path={['items', index, 'answers', 2, 'training_sentences']}
                    helperText='Please type at least 5 training sentences delimited by commas.'
                    multiline
                    rows={3}
                />
            </Grid>
        </>
    )
}